import React, { useState, useMemo, useCallback } from 'react';
import { DeliveryHistoryDetailRow } from '../types/deliveryHistoryDetail.types';

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

const DETAIL_COLUMNS = [
    { key: 'index', label: '순번' },
    { key: 'deliveryDate', label: '배달일자' },
    { key: 'deliveryStatus', label: '배달상태' },
    { key: 'deliveryMethod', label: '배달방법' },
    { key: 'distance', label: '거리(m)' },
    { key: 'goodsAmt', label: '상품가격' },
    { key: 'baseAmt', label: '기본단가' },
    { key: 'peakTimeAmt', label: '피크할증' },
    { key: 'weatherAmt', label: '기상할증' },
    { key: 'locationAmt', label: '지역할증' },
    { key: 'addAmt', label: '추가할증' },
    { key: 'deliveryAmt', label: '배달료' },
    { key: 'riderFaultYn', label: '라이더귀책' },
    { key: 'shopName', label: '가게명' },
    { key: 'orderTime', label: '주문시간' },
    { key: 'assingTime', label: '배차완료시간' },
    { key: 'shopArriTime', label: '가게도착시간' },
    { key: 'pickupTime', label: '픽업완료시간' },
    { key: 'deliveryTime', label: '배달완료시간' },
    { key: 'pickupAddr', label: '픽업주소' },
    { key: 'deliveryAddr', label: '배달주소' },
    { key: 'addAmtReason', label: '추가할증사유' },
] as const;

const compareValues = <T,>(a: T, b: T, key: keyof T, direction: SortDirection): number => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === 'asc' ? 1 : -1;
    if (bVal == null) return direction === 'asc' ? -1 : 1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal);
    const bStr = String(bVal);
    return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
};

interface DetailTableProps {
    results: DeliveryHistoryDetailRow[];
}

const DetailTable: React.FC<DetailTableProps> = ({ results }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({ columnKey: null, direction: null });

    const handleSort = useCallback((columnKey: string) => {
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    const sortedResults = useMemo(() => {
        if (!sortConfig.columnKey || !sortConfig.direction) return results;
        return [...results].sort((a, b) =>
            compareValues(a, b, sortConfig.columnKey as keyof DeliveryHistoryDetailRow, sortConfig.direction)
        );
    }, [results, sortConfig]);

    const renderSortIcon = (columnKey: string) => {
        if (columnKey === 'index') return null;
        const isSorted = sortConfig.columnKey === columnKey;
        const direction = isSorted ? sortConfig.direction : null;
        return (
            <span className="sort-icon">
                {direction === 'asc' && <i className="fas fa-sort-up"></i>}
                {direction === 'desc' && <i className="fas fa-sort-down"></i>}
                {!direction && <i className="fas fa-sort" style={{ opacity: 0.3 }}></i>}
            </span>
        );
    };

    return (
        <div className="delivery-history-detail-table-responsive">
            <table className="delivery-history-detail-table detail-table sortable-table">
                <thead>
                <tr>
                    {DETAIL_COLUMNS.map((col) => (
                        <th
                            key={col.key}
                            className={`${col.key !== 'index' ? 'sortable-header' : ''} ${sortConfig.columnKey === col.key ? 'sorted' : ''}`}
                            onClick={() => handleSort(col.key)}
                        >
                            {col.label}
                            {renderSortIcon(col.key)}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {sortedResults.length === 0 ? (
                    <tr>
                        <td colSpan={22} className="empty-state">
                            조회된 상세 내역이 없습니다.
                        </td>
                    </tr>
                ) : (
                    sortedResults.map((row, index) => (
                        <tr key={`${row.deliveryNo}-${row.riderId}`}>
                            <td>{index + 1}</td>
                            <td>{row.deliveryDate}</td>
                            <td>{row.deliveryStatus}</td>
                            <td>{row.deliveryMethod}</td>
                            <td>{row.distance.toLocaleString()}</td>
                            <td>{row.goodsAmt.toLocaleString()}</td>
                            <td>{row.baseAmt.toLocaleString()}</td>
                            <td>{row.peakTimeAmt.toLocaleString()}</td>
                            <td>{row.weatherAmt.toLocaleString()}</td>
                            <td>{row.locationAmt.toLocaleString()}</td>
                            <td>{row.addAmt.toLocaleString()}</td>
                            <td>{row.deliveryAmt.toLocaleString()}</td>
                            <td>{row.riderFaultYn}</td>
                            <td>{row.shopName}</td>
                            <td>{row.orderTime}</td>
                            <td>{row.assingTime}</td>
                            <td>{row.shopArriTime}</td>
                            <td>{row.pickupTime}</td>
                            <td>{row.deliveryTime}</td>
                            <td>{row.pickupAddr}</td>
                            <td>{row.deliveryAddr}</td>
                            <td>{row.addAmtReason}</td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        </div>
    );
};

export default DetailTable;
